import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../redux";
import {
  addCitizen,
  fetchCitizensBySearchParams,
  getCitizenById,
  updateCitizen,
} from "../redux/reducer/citizensReducer";

import TitleModal from "../components/common/titleModal";
import InputFiled from "../components/common/inputFiled";
import HeaderSection from "../components/header-section";
import ButtonSubmit from "../components/common/buttonSubmit";
import DatePickerInput from "../components/common/datePickerInput";
import HeaderModal from "../components/headerModal";
import moment from "moment";
import ErrorAlertMessage from "../components/alertMessageModal/ErrorAlertMessage";
import { useSelector } from "react-redux";
import {
  emailPattern,
  getErrorMessage,
  getFormattedDate,
  validateFiscalCode,
} from "../utils/helpers";
import FiscalCodeInputFiled from "../components/common/fiscalCodeInputFiled";

// Component starts
const CitizenForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Get citizen id from url.
   */
  const { id } = useParams();
  const location = useLocation();
  const { urlHistory } = location.state || {};

  /**
   * form state variables
   */
  const [fiscalCode, setFiscalCode] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [birthDate, setBirthDate] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  /**
   * users interaction state variables
   */
  const [fiscalCodeError, setFiscalCodeError] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [notificationType, setNotificationType] = useState("");

  /**
   * redux hooks.
   */
  const { citizens } = useSelector((state: any) => state);
  const { apiError } = citizens;

  /**
   * Disable date before 01-01-1910.
   */
  const minDate = moment("01-01-1910").format("DD-MM-YYYY");

  /**
   * fetch citizen by id
   */
  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const fetchData = async (id: any) => {
    const data = await dispatch(getCitizenById(id));
    if (data) {
      setName(data.name);
      setEmail(data.email);
      setPhone(data.phone);
      setSurname(data.surname);
      setAddress(data.crm.extraFields.address.value);
      if (data.crm.extraFields.birthdate.value) {
        setBirthDate(
          moment(data.crm.extraFields.birthdate.value).format("DD/MM/YYYY")
        );
      }
      setFiscalCode(data.crm.extraFields.fiscalcode.value);
    }
  };

  /**
   * validate fiscal-code and invalid email field before form submission
   */
  const validateSubmit = () => {
    if (emailError) return false;

    // check if users submit with empty fiscal code value
    const isValidFiscalCode = validateFiscalCode(fiscalCode);
    if (isValidFiscalCode) {
      setFiscalCodeError(isValidFiscalCode);
      return false;
    }

    if (id) {
      doUpdate();
    } else {
      doSubmit();
    }
  };

  /**
   * submit data
   */
  const doSubmit = async () => {
    setSpinner(true);
    try {
      const newId = await dispatch(
        addCitizen({
          name: name,
          surname: surname,
          email: email,
          fiscalcode: fiscalCode,
          birthdate: getFormattedDate(birthDate),
          address: address,
          phone: phone,
        })
      );
      if (newId) {
        redirectURL("success");
      } else {
        setNotificationType("error");
      }
    } catch (error) {
      console.log("add new citizen errors:", error);
    } finally {
      setSpinner(false);
    }
  };

  /**
   * update citizen data
   */
  const doUpdate = async () => {
    setSpinner(true);
    try {
      const updateId = await dispatch(
        updateCitizen(id, {
          name: name,
          surname: surname,
          email: email,
          fiscalcode: fiscalCode,
          birthdate: getFormattedDate(birthDate),
          address: address,
          phone: phone,
        })
      );
      if (updateId) {
        dispatch(fetchCitizensBySearchParams());
      }
      redirectURL(updateId ? "success" : "error");
    } catch (error) {
      console.log("update citizen errors:", error);
    } finally {
      setSpinner(false);
    }
  };

  /**
   * validate email format
   */
  const validateEmail = () => {
    if (email && !email.match(emailPattern())) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  /**
   * redirect to previous page
   */
  const redirectURL = (status: string) => {
    if (status) {
      navigate(`${urlHistory}?notification=${status}`);
    } else {
      navigate(urlHistory);
    }
  };

  /**
   * update notificationType value
   */
  const handleClose = () => {
    setNotificationType("");
  };

  /**
   * validate fiscalcode when 16 characters reaches.
   */
  const fiscalCodeLength = (value: string) => {
    if (value.length >= 16) {
      setFiscalCodeError(validateFiscalCode(value));
    }
  };

  /**
   * reset fiscal code error.
   */
  const resetFiscalCodeError = (value: string) => {
    if (value === "") setFiscalCodeError("");
  };

  /**
   * Get css styles
   */
  const getBorderStyles = (param: any) => {
    return apiError && apiError.param.includes(param)
      ? "border border-danger"
      : "";
  };

  return (
    <>
      {notificationType === "error" && (
        <ErrorAlertMessage
          icon="info"
          handleClose={handleClose}
          message={
            apiError ? getErrorMessage(apiError) : t("add-citizen-error")
          }
        />
      )}
      <HeaderModal
        textButtonRight={t("new-group-link-modal.error-back")}
        handleClick={() => redirectURL("")}
      />
      <div className="page-title">
        <HeaderSection
          visibleButton={false}
          titleSection={id ? t("edit-citizen") : t("add-citizen")}
        />
      </div>
      <div className="container-box mb-3 d-flex justify-content-center">
        <div className="content col-md-6">
          <div className="col-md-11 mt-4">
            <TitleModal title={t("citizen-register")} />
            <label
              className={`ms-2 label-sm ${
                fiscalCodeError ? "text-danger" : ""
              }`}
            >
              {t("fiscale-code")} *
            </label>
            <div className="mb-2">
              <FiscalCodeInputFiled
                value={fiscalCode}
                handleChange={(e: any) => (
                  setFiscalCode(e), resetFiscalCodeError(e), fiscalCodeLength(e)
                )}
                className={fiscalCodeError ? "border border-danger" : ""}
              />

              <div className="validation-errors-text-size text-danger mt-1 ms-2">
                {fiscalCodeError}
              </div>
            </div>
            <label className={`ms-2 label-sm`}>{t("name")}</label>
            <div className="mb-2">
              <InputFiled type="text" value={name} handleChange={setName} />
            </div>
            <label className={`ms-2 label-sm`}>{t("surname")}</label>
            <div className="mb-2">
              <InputFiled
                type="text"
                value={surname}
                handleChange={setSurname}
                className={getBorderStyles("surname")}
              />
            </div>

            <label className={`ms-2 label-sm`}>{t("date-of-birth")}</label>
            <div className="datepicker mb-2">
              <DatePickerInput
                disabled={false}
                minDate={minDate}
                maxDate={moment().toDate()}
                className="w-100 mt-0 ms-0 d-flex align-item-center justify-content-center input"
                handleChange={setBirthDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  birthDate ? moment(birthDate, "DD-MM-YYYY").toDate() : null
                }
              />
            </div>

            <TitleModal title={t("contact")} />
            <label className={`ms-2 label-sm`}>{t("email")}</label>
            <div className="mb-2">
              <InputFiled
                type="email"
                value={email}
                placeholder="email@esempio.com"
                handleChange={(e: any) => (setEmail(e), validateEmail())}
                className={getBorderStyles("email")}
              />
              {emailError && (
                <div className="validation-errors-text-size text-danger mt-1 ms-2">
                  {t("required.invalid-email")}
                </div>
              )}
            </div>
            <label className={`ms-2 label-sm`}>{t("address")}</label>
            <div className="mb-2">
              <InputFiled
                type="address"
                value={address}
                handleChange={setAddress}
                className={getBorderStyles("address")}
              />
            </div>

            <label className={`ms-2 label-sm`}>{t("phone")}</label>
            <div className="mb-2">
              <InputFiled
                type="phone"
                value={phone}
                handleChange={setPhone}
                className={getBorderStyles("phone")}
              />
            </div>

            <div className="d-flex pt-4 pb-4 justify-content-center">
              <div className="w-100 d-flex justify-content-center">
                <ButtonSubmit
                  className="btn-width"
                  textButton={t("save")}
                  spinner={spinner}
                  handleChange={validateSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CitizenForm;
